import { DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Spin, Tooltip, message } from '@surecompanies/core_components'
import dayjs from 'dayjs'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
import React, { useState } from 'react'
import { getCensusSnapshotUrl } from './census-run-api'
import { withCompanyName } from './client-key-label'
import { StepDetail } from './ProcessingDetails'
import Section from './Section'
import SnapshotPreviewModal from './SnapshotPreviewModal'
import StageRail, { ACCENT } from './StageRail'
import { CensusRunStepDto, JsonValue } from './types'

/**
 * The census file stages, in the order the backend records them. Both channels
 * share this list: a stage an API integration run never performs is recorded as
 * SKIPPED rather than left out, so it still appears here with its reason.
 */
/**
 * `addedAt` is the ISO date a stage started being recorded. A run older than
 * that never had the stage, which is not the same as never reaching it, so the
 * row says so rather than reading as a failure. Set it when adding a stage to
 * an already-released timeline; leave it off for a stage that has always run.
 */
export const TIMELINE_STAGES: {
  seq: number
  stepKey: string
  name: string
  addedAt?: string
}[] = [
  { seq: 1, stepKey: 'RAW_FILE_RECEIVED', name: 'Raw File Received' },
  { seq: 2, stepKey: 'MAPPING_APPLIED', name: 'Mapping Applied' },
  { seq: 3, stepKey: 'ELIGIBILITY_FILTER', name: 'Eligibility Filter' },
  // The stored key stays COMPANY_IGNORE_RULES. The label says "loaded"
  // because that is all this stage does: the rules themselves take effect
  // during validation, and a name that implied otherwise was read as a step
  // that had somehow done nothing.
  { seq: 4, stepKey: 'COMPANY_IGNORE_RULES', name: 'Ignore Rules Loaded' },
  { seq: 5, stepKey: 'VALIDATION', name: 'Validation Complete' },
  { seq: 6, stepKey: 'SAVED_TO_DATABASE', name: 'Saved to Database' },
  { seq: 7, stepKey: 'SUBMITTED_TO_CMV2', name: 'Submitted to CMv2' },
]

/** What a data artifact is called on screen. A stage with one artifact says
 *  "View snapshot"; a stage with several names each one. */
const SLOT_LABELS: Record<string, string> = {
  ELIGIBLE: 'Eligible rows',
  INELIGIBLE: 'Ineligible',
  ERRORS: 'Errors',
  RAW: 'Raw rows',
  MAPPED: 'Mapped rows',
  METADATA: 'Ignored changes',
  FINAL: 'Valid rows',
}

/**
 * The outcomes a stage can end in, shown behind a ? next to its name.
 *
 * Written down rather than derived: it says what is possible, so a reader can
 * tell an unusual result from an impossible one. Only stages whose outcomes are
 * not obvious from the result line are listed.
 *
 * Kept per channel: the two paths reach the same stages through different code,
 * and an outcome that is routine on one can be impossible on the other.
 */
const AMA_STAGE_OUTCOMES: Record<string, string[]> = {
  MAPPING_APPLIED: [
    'OK · every row mapped to the census schema',
    'WARN · some rows carry a mapping error: a rule could not map a value, wrote the reason on the row and let it through',
    'ERROR · only one failure stops this stage: the client has neither an active mapping template nor a hardcoded mapping',
  ],
  COMPANY_IGNORE_RULES: [
    'SKIPPED · always — this stage loads the rules, and validation is where they take effect',
    'The result line says which of three cases produced an empty field list: no active rule, a rule that does not cover AMA, or a rule that ignores nothing',
  ],
  ELIGIBILITY_FILTER: [
    'OK · rules ran, no row held back',
    'OK · rules ran, some rows held back as ineligible',
    'SKIPPED · no active template, so every row passed',
    'WARN · the template could not be read, so nothing was filtered and every row went on',
  ],
}

const API_STAGE_OUTCOMES: Record<string, string[]> = {
  MAPPING_APPLIED: [
    'OK · every row mapped to the census schema',
    'WARN · some rows carry a mapping error: a rule could not map a value, wrote the reason on the row and let it through',
    'ERROR · the client has neither an active mapping template nor a hardcoded mapping',
  ],
  ELIGIBILITY_FILTER: [
    'OK · rules ran, no row held back',
    'OK · rules ran, some rows held back as ineligible',
    'ERROR · the client has no eligibility rules from any source, which stops the run',
  ],
  COMPANY_IGNORE_RULES: [
    'SKIPPED · always — this stage loads the rules, and validation is where they take effect',
    'The result line says which of three cases produced an empty field list: no active rule, a rule that does not cover API integration, or a rule that ignores nothing',
  ],
  SUBMITTED_TO_CMV2: [
    'SKIPPED · always — an API file reaches Census Management directly, with no submit step',
  ],
}

const STAGE_OUTCOMES: Record<string, Record<string, string[]>> = {
  AMA: AMA_STAGE_OUTCOMES,
  API: API_STAGE_OUTCOMES,
}

function StageHelp({
  stepKey,
  channel,
}: {
  stepKey: string
  channel?: string
}) {
  const outcomes = channel ? STAGE_OUTCOMES[channel]?.[stepKey] : undefined
  if (!outcomes) {
    return null
  }
  return (
    <Tooltip
      placement="right"
      title={
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            What this stage can end as
          </div>
          {outcomes.map((outcome) => (
            <div key={outcome}>· {outcome}</div>
          ))}
        </div>
      }
    >
      <QuestionCircleOutlined
        style={{ marginLeft: 6, color: '#9ca3af', cursor: 'help' }}
      />
    </Tooltip>
  )
}

interface Artifact {
  slot: string
  file: string
  rowCount?: number
  columnCount?: number
  sizeBytes?: number
  configName?: string
  configVersion?: string
}

const isArtifact = (value: unknown): value is Artifact =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Artifact).file === 'string' &&
  typeof (value as Artifact).slot === 'string'

const parseArtifacts = (value: unknown): Artifact[] =>
  Array.isArray(value) && value.every(isArtifact) ? value : []

const formatTime = (value?: string): string =>
  value ? dayjs(value).format('h:mm:ss A') : ''

const formatDurationLong = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`
  // Rounded to whole seconds once, before the split into minutes. Rounding the
  // leftover of an already-split value carries to 60, printing "1m 60s" for a
  // run a fraction short of two minutes — and "60s" for one short of one.
  const totalSeconds = Math.round(ms / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`
}

/** The pill that holds a snapshot: a label to open it, an icon to download it.
 *  Shared with the config chips so every artifact on the row looks the same. */
const CHIP: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '3px 6px 3px 12px',
  borderRadius: 999,
  border: '1px solid #e5e7eb',
  background: '#f9fafb',
  fontSize: 12,
}

const CHIP_DOWNLOAD: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: '50%',
  background: '#d7ece9',
  color: ACCENT,
  cursor: 'pointer',
  fontSize: 11,
}

/** The download icon of a chip. The chip itself is hidden without permission. */
function ChipDownloadButton({
  file,
  onDownload,
}: {
  file: string
  onDownload: (file: string) => void
}) {
  return (
    <span
      role="button"
      style={CHIP_DOWNLOAD}
      onClick={() => onDownload(file)}
      title={`Download ${file}`}
    >
      <DownloadOutlined />
    </span>
  )
}

export default function CensusFileTimeline({
  steps,
  isLoading,
  isError,
  runId,
  channel,
  clientPublicKey,
  clientCompanyName,
}: {
  steps: CensusRunStepDto[]
  isLoading: boolean
  isError: boolean
  runId: string
  channel?: string
  clientPublicKey?: string
  clientCompanyName?: string
}) {
  const [viewed, setViewed] = useState<{
    stepKey: string
    stageName: string
    file: string
  } | null>(null)
  // Snapshots carry employee data, so the download button is hidden from users
  // not cleared for US-restricted data. UI only — the API guard is the boundary.
  const { allowed: canDownload } = usePermission(
    RbacActions.Manage,
    RbacSubjects.UsRestrictedData
  )

  const download = async (stepKey: string, file: string) => {
    // The tab is opened inside the click, before the await. Opening it after
    // the presigned URL comes back puts the call outside the user gesture, and
    // a blocked popup returns null rather than throwing, so the catch below
    // would never run and the click would look like it did nothing.
    const tab = window.open('', '_blank', 'noopener')
    try {
      const { url } = await getCensusSnapshotUrl(runId, stepKey, file)
      if (tab) {
        tab.location.href = url
        return
      }
      window.open(url, '_blank', 'noopener')
    } catch {
      tab?.close()
      message.error(`Could not open ${file}`)
    }
  }

  if (isLoading) {
    return (
      <Section title="Census file timeline">
        <div style={{ padding: '24px 0', textAlign: 'center' }}>
          <Spin />
        </div>
      </Section>
    )
  }

  if (isError) {
    return (
      <Section title="Census file timeline">
        <div style={{ padding: '16px 0', color: '#7a1f22' }}>
          Could not load the timeline for this run.
        </div>
      </Section>
    )
  }

  const byStepKey = new Map(steps.map((step) => [step.stepKey, step]))
  // The run's own clock, read from what it recorded rather than passed in, so
  // a stage added later can tell "this run predates me" from "never got there".
  const runStartedAt = steps
    .map((step) => step.startedAt)
    .sort()
    .at(0)
  const predatesStage = (addedAt?: string): boolean =>
    Boolean(addedAt && runStartedAt && runStartedAt < addedAt)
  const rowsProcessed = byStepKey.get('RAW_FILE_RECEIVED')?.detail?.rowsIn
  const totalMs = steps.reduce((sum, step) => sum + (step.durationMs ?? 0), 0)

  return (
    <Section
      title="Census file timeline"
      extra={
        <div style={{ display: 'flex', gap: 28 }}>
          {typeof rowsProcessed === 'number' && (
            <Figure label="Rows processed" value={String(rowsProcessed)} />
          )}
          <Figure label="Duration" value={formatDurationLong(totalMs)} />
        </div>
      }
    >
      <div style={{ paddingTop: 8 }}>
        {TIMELINE_STAGES.map(({ seq, stepKey, name, addedAt }) => (
          <StageRow
            key={stepKey}
            name={name}
            stepKey={stepKey}
            isLast={seq === TIMELINE_STAGES.length}
            step={byStepKey.get(stepKey)}
            predatesStage={predatesStage(addedAt)}
            channel={channel}
            clientPublicKey={clientPublicKey}
            clientCompanyName={clientCompanyName}
            onView={(file) => setViewed({ stepKey, stageName: name, file })}
            canDownload={canDownload}
            onDownload={(file) => download(stepKey, file)}
          />
        ))}
      </div>
      {viewed && (
        <SnapshotPreviewModal
          runId={runId}
          stepKey={viewed.stepKey}
          stageName={viewed.stageName}
          file={viewed.file}
          onClose={() => setViewed(null)}
        />
      )}
    </Section>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: '#9ca3af',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700 }}>{value}</div>
    </div>
  )
}

function StageRow({
  name,
  stepKey,
  step,
  isLast,
  predatesStage,
  channel,
  clientPublicKey,
  clientCompanyName,
  canDownload,
  onView,
  onDownload,
}: {
  name: string
  stepKey: string
  step?: CensusRunStepDto
  isLast: boolean
  predatesStage: boolean
  channel?: string
  clientPublicKey?: string
  clientCompanyName?: string
  canDownload: boolean
  onView: (file: string) => void
  onDownload: (file: string) => void
}) {
  const detail = step?.detail ?? {}
  const artifacts = parseArtifacts(detail.artifacts)
  const configs = artifacts.filter((artifact) => artifact.slot === 'CONFIG')
  const data = artifacts.filter((artifact) => artifact.slot !== 'CONFIG')
  const submissions = Array.isArray(detail.submissions)
    ? detail.submissions
    : []
  const rowsIn = typeof detail.rowsIn === 'number' ? detail.rowsIn : undefined
  const rowsOut =
    typeof detail.rowsOut === 'number' ? detail.rowsOut : undefined
  // The artifacts, the submissions and the row counts each have their own place
  // on this row, so the detail panel carries only what is left.
  const rest = Object.fromEntries(
    Object.entries(detail).filter(
      ([key]) =>
        !['artifacts', 'submissions', 'rowsIn', 'rowsOut'].includes(key)
    )
  )

  return (
    <div style={{ display: 'flex', gap: 14 }}>
      <StageRail status={step?.status} isLast={isLast} />

      <div
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          paddingBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 14.5,
              fontWeight: 600,
              color: step ? undefined : '#9ca3af',
            }}
          >
            {name}
            <StageHelp stepKey={stepKey} channel={channel} />
          </div>
          <div style={{ color: '#6b7280', fontSize: 12.5 }}>
            {withCompanyName(
              step?.result,
              clientPublicKey,
              clientCompanyName
            ) ?? (predatesStage ? 'Added after this run' : 'Not reached')}
            {step?.errorCode && (
              <span style={{ marginLeft: 8, fontFamily: 'monospace' }}>
                ({step.errorCode})
              </span>
            )}
          </div>
          {typeof rowsIn === 'number' && typeof rowsOut === 'number' && (
            <div
              style={{ color: '#6b7280', fontSize: 12.5, marginTop: 2 }}
              title="Rows this stage was given, and rows it passed on"
            >
              {rowsIn} in
              <span style={{ margin: '0 6px', color: '#d1d5db' }}>&rarr;</span>
              {rowsOut} out
              {rowsIn !== rowsOut && (
                <span style={{ marginLeft: 8, color: '#9ca3af' }}>
                  ({rowsIn - rowsOut} held back)
                </span>
              )}
            </div>
          )}
          {canDownload && configs.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginTop: 6,
              }}
            >
              {configs.map((config) => (
                <ConfigChip
                  key={config.file}
                  file={config.file}
                  onView={onView}
                  onDownload={onDownload}
                />
              ))}
            </div>
          )}
          <StepDetail detail={rest} />
          {submissions.map((submission, index) => (
            <div key={index}>
              <StepDetail detail={submission as Record<string, JsonValue>} />
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ color: '#9ca3af', fontSize: 12.5 }}>
            {formatTime(step?.endedAt)}
          </span>
          {canDownload &&
            (data.length === 0 ? (
              <span style={{ color: '#d1d5db', fontSize: 12.5 }}>
                No snapshot
              </span>
            ) : (
              data.map((artifact) => (
                <span key={artifact.file} style={CHIP}>
                  <a
                    onClick={() => onView(artifact.file)}
                    style={{ color: ACCENT }}
                    title={`${artifact.file}${
                      artifact.rowCount === undefined
                        ? ''
                        : ` · ${artifact.rowCount} row(s)`
                    }`}
                  >
                    {data.length === 1
                      ? 'View snapshot'
                      : (SLOT_LABELS[artifact.slot] ?? artifact.slot)}
                  </a>
                  <ChipDownloadButton
                    file={artifact.file}
                    onDownload={onDownload}
                  />
                </span>
              ))
            ))}
        </div>
      </div>
    </div>
  )
}

/** A config file the stage used, e.g. the eligibility rules it applied. */
function ConfigChip({
  file,
  onView,
  onDownload,
}: {
  file: string
  onView: (file: string) => void
  onDownload: (file: string) => void
}) {
  return (
    <span style={CHIP}>
      <a
        onClick={() => onView(file)}
        style={{ fontFamily: 'monospace', color: ACCENT }}
      >
        {file}
      </a>
      <ChipDownloadButton file={file} onDownload={onDownload} />
    </span>
  )
}
